import {
  closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent,
} from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useEffect, useRef, useState } from "react";
import type { Step } from "../../../shared/types";
import { imageUrl } from "../../api";
import { IconClick, IconLayers, IconMessage, IconPlus, IconTimer, IconX } from "../../icons";
import { CapturePicker } from "../CapturesPage";
import { useEditor } from "../DemoEditor";
import { DRAWER_ID } from "./compact";
import { pathsOf } from "../../../shared/tour";
import { PathBar } from "./paths";
import { MEDIA_ACCEPT, type StepActions } from "./useStepActions";

export const kindLabel = (s: Step) =>
  s.textCard
    ? s.textCard.role === "intro"
      ? "Intro"
      : s.textCard.role === "outro"
      ? "Outro"
      : "Text"
    : s.captureKey
    ? "HTML"
    : s.mediaType === "video"
    ? "Video"
    : "Screenshot";
const two = (n: number) => String(n).padStart(2, "0");

interface Props {
  seq: Step[];
  pathId: string | null;
  onPath: (v: string | null) => void;
  stepId: string | undefined;
  onSelect: (id: string) => void;
  actions: StepActions;
  /** Compact Studio: the list is a drawer over the canvas, open or not, with its own close button. */
  drawer?: { open: boolean; onClose: () => void };
}

/**
 * Left pane: the path being edited, then its screens in order (only its own: a screen on another path only is not
 * listed). Click to edit, drag the grip to reorder, drop files to add.
 */
export default function StepList({ seq, pathId, onPath, stepId, onSelect, actions, drawer }: Props) {
  const { bundle } = useEditor();
  const [order, setOrder] = useState<string[]>(seq.map((s) => s.id));
  const [over, setOver] = useState(false);
  const [menu, setMenu] = useState(false);
  const [picking, setPicking] = useState(false);
  const [including, setIncluding] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => setOrder(seq.map((s) => s.id)), [seq]);
  const byId = new Map(bundle.steps.map((s) => [s.id, s]));

  useEffect(() => {
    if (!menu) return;
    const close = (e: MouseEvent) => { if (!menuRef.current?.contains(e.target as Node)) setMenu(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setMenu(false); };
    window.addEventListener("mousedown", close);
    window.addEventListener("keydown", esc);
    return () => { window.removeEventListener("mousedown", close); window.removeEventListener("keydown", esc); };
  }, [menu]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = async (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    const next = arrayMove(order, order.indexOf(String(e.active.id)), order.indexOf(String(e.over.id)));
    setOrder(next);
    if (!(await actions.reorder(next))) setOrder(seq.map((s) => s.id));
  };

  const hasFiles = (e: React.DragEvent) => [...e.dataTransfer.types].includes("Files");

  return (
    <aside
      id={DRAWER_ID.steps}
      className={`studio-steps ${over ? "over" : ""} ${drawer?.open ? "open" : ""}`}
      aria-label="Tour steps"
      onDragOver={(e) => { if (hasFiles(e)) { e.preventDefault(); setOver(true); } }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOver(false); }}
      onDrop={(e) => { if (hasFiles(e)) { e.preventDefault(); setOver(false); void actions.upload(e.dataTransfer.files); } }}
    >
      <header className="studio-pane-head">
        <IconLayers /><h2>Tour steps</h2><span className="count">{seq.length}</span>
        {drawer && <button type="button" className="drawer-x" onClick={drawer.onClose} aria-label="Close the screen list" title="Close (Esc)"><IconX /></button>}
      </header>
      <div className="studio-paths"><PathBar value={pathId} onChange={onPath} /></div>

      <div className="studio-step-scroll">
        {order.length === 0 ? (
          <p className="small muted studio-empty">No screens yet. Add screenshots or a captured page, or drop files here.</p>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={order} strategy={verticalListSortingStrategy}>
              <ol className="st-list">
                {order.map((id, i) => {
                  const s = byId.get(id);
                  if (!s) return null;
                  return <StepItem key={id} step={s} index={i} selected={id === stepId} onSelect={() => onSelect(id)} />;
                })}
              </ol>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {actions.progress && <div className="upload-progress studio-note" role="status">{actions.progress}</div>}
      {actions.error && <div className="error-box studio-note">{actions.error}</div>}

      <footer className="studio-step-foot" ref={menuRef}>
        {menu && (
          <div className="menu" role="menu">
            <button role="menuitem" onClick={() => { setMenu(false); fileRef.current?.click(); }}>
              <b>Upload screenshots</b><span className="small muted">PNG, JPEG, WebP, GIF</span>
            </button>
            <button role="menuitem" onClick={() => { setMenu(false); setPicking(true); }}>
              <b>Add an HTML capture</b><span className="small muted">A live page recorded with the extension</span>
            </button>
            <button role="menuitem" onClick={() => { setMenu(false); void actions.addTextCard("intro"); }}>
              <b>Add Intro screen</b><span className="small muted">Welcome slide with headline and CTA button</span>
            </button>
            <button role="menuitem" onClick={() => { setMenu(false); void actions.addTextCard("outro"); }}>
              <b>Add Outro screen</b><span className="small muted">Conclusion slide with wrap-up & CTA links</span>
            </button>
            <button role="menuitem" onClick={() => { setMenu(false); void actions.addTextCard("card"); }}>
              <b>Add Text card</b><span className="small muted">Standalone message or transition slide</span>
            </button>
            <button role="menuitem" onClick={() => { setMenu(false); setIncluding(true); }}>
              <b>Copy a screen from another path</b><span className="small muted">Its own copy here, to edit on this path only</span>
            </button>
          </div>
        )}
        <button className="btn st-add" aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu((m) => !m)}><IconPlus />Add step</button>
        
        {stepId && byId.has(stepId) && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "8px" }}>
            <button className="btn btn-sm btn-danger" onClick={async () => {
              const s = byId.get(stepId)!;
              const idx = seq.findIndex(x => x.id === stepId);
              if (idx === -1) return;
              const removed = await actions.remove(s, idx);
              if (removed) {
                onSelect((seq[idx + 1] ?? seq[idx - 1])?.id ?? "");
              }
            }}>Delete step</button>
            <button className="btn btn-sm" onClick={() => void actions.duplicate(byId.get(stepId)!)}>Duplicate</button>
          </div>
        )}

        <input ref={fileRef} type="file" multiple hidden accept={MEDIA_ACCEPT} onChange={(e) => { void actions.upload(e.target.files); e.target.value = ""; }} />
      </footer>
      {picking && <CapturePicker onClose={() => setPicking(false)} onPick={(c) => { setPicking(false); void actions.addCapture(c); }} />}
      {including && <ScreenPicker seq={seq} onClose={() => setIncluding(false)} onPick={(id) => { setIncluding(false); void actions.include(id); }} />}
    </aside>
  );
}

/** Other paths' steps, to copy one onto this path (its own copy: the same media, edited here only). */
function ScreenPicker({ seq, onPick, onClose }: { seq: Step[]; onPick: (id: string) => void; onClose: () => void }) {
  const { bundle } = useEditor();
  const here = new Set(seq.map((s) => s.id));
  const others = bundle.steps.filter((s) => !here.has(s.id));
  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label="Copy a screen from another path" onPointerDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-card narrow">
        <div className="row"><h2>Copy a screen from another path</h2><span className="spacer" /><button className="btn btn-ghost" onClick={onClose}>Close</button></div>
        <p className="small muted">This path gets its own copy, with its hotspots and callout: changes to it here don't touch the other path.</p>
        {others.length === 0 ? <p className="muted">No other path has any screens yet.</p> : (
          <div className="cap-grid">
            {others.map((s) => {
              const on = pathsOf(bundle, s.id);
              return (
                <button key={s.id} type="button" className="cap-card" onClick={() => onPick(s.id)}>
                  {s.textCard
                    ? <div className="cap-shot cap-shot-text"><span>{s.textCard.title}</span></div>
                    : <div className="cap-shot" style={s.mediaType === "video" ? undefined : { backgroundImage: `url(${imageUrl(s.imageKey)})` }} />}
                  <div className="cap-body"><b>{s.caption || s.title || kindLabel(s)}</b><span className="small muted">{on.length ? `On ${on.join(", ")}` : "Not on any path"}</span></div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function StepItem({ step, index, selected, onSelect }: { step: Step; index: number; selected: boolean; onSelect: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: step.id });
  const secs = step.autoAdvanceMs ? `${step.autoAdvanceMs / 1000}s` : null;
  const empty = !step.textCard && step.hotspots.length === 0 && !step.callout && !secs;
  const chapter = step.chapterTitle?.trim();

  return (
    <li ref={setNodeRef} className={`st-item-wrap ${isDragging ? "dragging" : ""}`}
      style={{ transform: CSS.Transform.toString(transform), transition }}>
      {chapter && (
        <div className="st-chapter-header" title={`Starts chapter: ${chapter}`}>
          <span className="st-chapter-pill">Chapter</span>
          <span className="st-chapter-title">{chapter}</span>
        </div>
      )}
      <div className={`st-item ${selected ? "on" : ""}`}>
        <button type="button" className="st-grip" {...attributes} {...listeners}
          aria-label={`Drag to reorder screen ${index + 1}`} title="Drag to reorder">
          <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor" aria-hidden><circle cx="6" cy="4" r="1.1" /><circle cx="10" cy="4" r="1.1" /><circle cx="6" cy="8" r="1.1" /><circle cx="10" cy="8" r="1.1" /><circle cx="6" cy="12" r="1.1" /><circle cx="10" cy="12" r="1.1" /></svg>
        </button>
        <button type="button" className="st-main" onClick={onSelect} aria-current={selected ? "true" : undefined}>
          <span className="st-top">
            <span className="st-num">{two(index + 1)}</span>
            <span className={`st-kind ${step.textCard ? "textcard" : step.captureKey ? "html" : step.mediaType === "video" ? "video" : ""}`}>{kindLabel(step)}</span>
            <span className={`st-meta ${empty ? "warn" : ""}`}>
              {step.callout && <span className="st-badge" title="Has a callout"><IconMessage className="icon icon-xs" /></span>}
              {step.hotspots.length > 0 && <span className="st-badge" title={`${step.hotspots.length} hotspot${step.hotspots.length === 1 ? "" : "s"}`}><IconClick className="icon icon-xs" />{step.hotspots.length}</span>}
              {secs && <span className="st-badge" title="Advances automatically"><IconTimer className="icon icon-xs" />{secs}</span>}
              {empty && "No hotspots"}
            </span>
          </span>
          <span className={`st-thumb ${step.textCard ? "st-thumb-text" : ""}`} style={step.textCard || step.mediaType === "video" ? undefined : { backgroundImage: `url(${imageUrl(step.imageKey)})` }}>
            {step.textCard ? (
              <div className={`st-thumb-card ts-${step.textCard.role}`}>
                <span className="st-thumb-badge">{step.textCard.role === "intro" ? "Intro" : step.textCard.role === "outro" ? "Outro" : "Card"}</span>
                <span className="st-thumb-title">{step.textCard.title}</span>
              </div>
            ) : step.mediaType === "video" ? (
              <video src={imageUrl(step.imageKey)} muted preload="metadata" />
            ) : null}
            {step.caption && !step.textCard && <span className="st-cap">{step.caption}</span>}
          </span>
        </button>
      </div>
    </li>
  );
}
